/**
 * ProfileHeader — light gray card with 4px gold left border, shown at top of
 * every right detail panel. Matches ContactsPage/SubscribersPage profile header.
 */

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { colors, fonts } from './colors';

interface MetaItem {
  icon?: IconDefinition;
  text: React.ReactNode;
}

interface ProfileHeaderProps {
  title: React.ReactNode;            // e.g. contact name, transaction address
  metaItems?: MetaItem[];            // row of icon+text items under title
  rightContent?: React.ReactNode;    // actions, status, value on the right
  badges?: React.ReactNode;          // optional row above actions
}

const ProfileHeader: React.FC<ProfileHeaderProps> = ({ title, metaItems = [], rightContent, badges }) => {
  return (
    <div
      style={{
        background: colors.card,
        borderLeft: `4px solid ${colors.gold}`,
        borderRadius: '8px',
        padding: '20px 24px',
        margin: '24px 24px 16px 24px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1
            style={{
              fontFamily: fonts.display,
              fontSize: '32px',
              color: colors.navy,
              margin: 0,
              marginBottom: metaItems.length ? '10px' : 0,
              letterSpacing: '0.5px',
              lineHeight: 1.1,
            }}
          >
            {title}
          </h1>
          {metaItems.length > 0 && (
            <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', alignItems: 'center' }}>
              {metaItems.map((item, i) => (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '14px',
                    color: '#555',
                  }}
                >
                  {item.icon && (
                    <FontAwesomeIcon icon={item.icon} style={{ color: colors.gold2, fontSize: '13px' }} />
                  )}
                  <span>{item.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          {badges && <div style={{ marginBottom: '8px' }}>{badges}</div>}
          {rightContent}
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;
